import {Actions, can} from '@redstack/shared'
import * as Sentry from '@sentry/react'
import type {QueryClient} from '@tanstack/react-query'
import {
	createRootRouteWithContext,
	Navigate,
	Outlet,
	useLocation,
} from '@tanstack/react-router'
import {
	Activity,
	Building2,
	CalendarDays,
	Gauge,
	LayoutGrid,
	Loader2,
	Settings,
	UserCog,
	Users,
} from 'lucide-react'
import {useEffect, useMemo} from 'react'
import {
	AppSidebar,
	type SidebarNavGroup,
	type SidebarNavLink,
} from '@/components/AppSidebar'
import {MobileTabBar} from '@/components/MobileTabBar'
import {NoAccessScreen} from '@/components/NoAccessScreen'
import {SidebarInset, SidebarProvider} from '@/components/ui/sidebar'
import {formatUserName} from '@/features/permits/utils/formatters'
import {useCurrentUser} from '@/hooks/users'
import {ApiError} from '@/lib/api/client'
import {identifyUser, setTag} from '@/lib/clarity'

function RootComponent() {
	const {
		data: currentUser,
		isPending: identityPending,
		isError: identityError,
		error: identityErrorValue,
		refetch: refetchIdentity,
	} = useCurrentUser()
	const location = useLocation()
	const role = currentUser?.role

	const isDeveloper =
		(currentUser?.realUserRole ?? currentUser?.role) === 'DEVELOPER'

	// Org-less developer session (not impersonating anyone): only the
	// org-independent pages (/admin, /monitoring) are meaningful — every
	// tenant-scoped query would 403-burst without an org to scope to.
	const orgless = Boolean(
		currentUser &&
			currentUser.role === 'DEVELOPER' &&
			!currentUser.organizationId &&
			!currentUser.impersonating,
	)

	const navGroups = useMemo<SidebarNavGroup[]>(() => {
		const workspace: SidebarNavLink[] = orgless
			? []
			: [
					{to: '/today', label: 'Today', Icon: CalendarDays},
					{to: '/board', label: 'Board', Icon: LayoutGrid},
				]

		const admin: SidebarNavLink[] = [
			!orgless &&
				can(role, Actions.DASHBOARD_VIEW) && {
					to: '/dashboard',
					label: 'Dashboard',
					Icon: Gauge,
				},
			!orgless &&
				can(role, Actions.USERS_MANAGE) && {
					to: '/users',
					label: 'Users',
					Icon: Users,
				},
			!orgless &&
				can(role, Actions.SETTINGS_MANAGE) && {
					to: '/settings',
					label: 'Settings',
					Icon: Settings,
				},
			isDeveloper && {
				to: '/admin',
				label: 'Organizations',
				Icon: Building2,
			},
			isDeveloper && {
				to: '/admin/staff',
				label: 'Staff',
				Icon: UserCog,
			},
			isDeveloper && {
				to: '/monitoring',
				label: 'Monitoring',
				Icon: Activity,
			},
		].filter((link): link is SidebarNavLink => Boolean(link))

		return [
			{label: 'Workspace', links: workspace},
			{label: 'Admin', links: admin},
		]
	}, [role, isDeveloper, orgless])

	// Tag Clarity sessions with user role for filtering
	useEffect(() => {
		if (currentUser?.role) {
			setTag('role', currentUser.role)
		}
	}, [currentUser?.role])

	// Re-identify user on each page navigation (Microsoft's recommended best practice)
	useEffect(() => {
		if (currentUser?.id) {
			identifyUser(
				currentUser.id,
				undefined,
				location.pathname,
				formatUserName(currentUser),
			)
		}
	}, [currentUser, location.pathname])

	// Identify the user to Sentry so the Contact us feedback form prefills
	// name/email (via useSentryUser) and error reports carry user context.
	useEffect(() => {
		if (currentUser?.id) {
			Sentry.setUser({
				id: currentUser.id,
				email: currentUser.email ?? undefined,
				username: formatUserName(currentUser),
			})
		}
	}, [currentUser])

	// Hold until identity resolves. Rendering the layout during the /me fetch
	// would mount org-scoped pages before we know whether this account HAS an
	// org — an org-less developer's queries would 403-burst, and a real user's
	// would race the developer-landing swap below. Identity first, data second.
	if (identityPending) {
		return (
			<div className='flex h-dvh items-center justify-center bg-background'>
				<Loader2 className='size-5 animate-spin text-muted-foreground' />
			</div>
		)
	}

	// A 401 (account not linked to an org) or 403 (organization archived) on
	// /me is a settled "no access" state, not a transient failure. Show a clear
	// dead-end with a way out (sign out), not a Retry that fails identically
	// forever. The 403 renders the server's own message — the API's archive
	// rejection copy names the reason.
	if (
		identityError &&
		identityErrorValue instanceof ApiError &&
		(identityErrorValue.status === 401 || identityErrorValue.status === 403)
	) {
		return identityErrorValue.status === 403 ? (
			<NoAccessScreen
				title='Organization archived'
				message={identityErrorValue.message}
			/>
		) : (
			<NoAccessScreen />
		)
	}

	// Any other error (network drop, 5xx) is genuinely transient — keep Retry.
	if (identityError) {
		return (
			<div className='flex h-dvh items-center justify-center bg-background p-4'>
				<div className='space-y-3 text-center'>
					<p className='text-sm text-muted-foreground'>
						Couldn't load your account. Check your connection and try again.
					</p>
					<button
						type='button'
						onClick={() => refetchIdentity()}
						className='rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted'
					>
						Retry
					</button>
				</div>
			</div>
		)
	}

	// Unscoped DEVELOPER: render the normal app shell, but keep tenant-scoped
	// routes unreachable — deep links and the `/` → /today redirect would mount
	// pages whose queries all 403 without an org. The admin console (/admin) is
	// the natural home; the sidebar-footer DevUserSwitcher remains the way into
	// a tenant view.
	const orglessAllowed =
		location.pathname.startsWith('/admin') ||
		location.pathname.startsWith('/monitoring')

	return (
		<div className='flex h-dvh flex-col overflow-hidden bg-background'>
			<SidebarProvider className='min-h-0 flex-1'>
				<AppSidebar navGroups={navGroups} />
				<SidebarInset className='min-h-0 min-w-0 overflow-hidden'>
					<div className='min-h-0 flex-1 overflow-hidden'>
						{orgless && !orglessAllowed ? <Navigate to='/admin' /> : <Outlet />}
					</div>
					<MobileTabBar />
				</SidebarInset>
			</SidebarProvider>
		</div>
	)
}

export const Route = createRootRouteWithContext<{queryClient: QueryClient}>()({
	component: RootComponent,
})
